// src/context/NotificationContext.js
import React, { createContext, useContext, useState, useRef, useEffect } from 'react';
import { ThemeContext } from "./ThemeContext"; // ← Theme Context Import

const NotificationContext = createContext();

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  // ✅ FIX: Renamed state to avoid conflict with function name
  const [isNotificationVisible, setIsNotificationVisible] = useState(false);
  const [notificationData, setNotificationData] = useState({
    type: 'info',
    title: '',
    message: '',
    duration: 5000
  });
  
  const timeoutRef = useRef(null);

  // ─────────────────────────────────────────────────────────────
  // SHOW NOTIFICATION
  // ─────────────────────────────────────────────────────────────
  const showNotificationFn = (type, title, message, duration = 5000) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    setNotificationData({ type, title, message, duration });
    setIsNotificationVisible(true); // ✅ Use renamed setter
    
    if (duration > 0) {
      timeoutRef.current = setTimeout(() => {
        setIsNotificationVisible(false);
      }, duration);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // HIDE NOTIFICATION
  // ─────────────────────────────────────────────────────────────
  const hideNotification = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsNotificationVisible(false);
  };

  // ─────────────────────────────────────────────────────────────
  // NOTIFICATION TYPES
  // ─────────────────────────────────────────────────────────────
  const showSuccess = (title, message, duration = 5000) => 
    showNotificationFn('success', title, message, duration);

  const showError = (title, message, duration = 5000) => 
    showNotificationFn('error', title, message, duration);

  const showWarning = (title, message, duration = 5000) => 
    showNotificationFn('warning', title, message, duration);

  const showInfo = (title, message, duration = 5000) => 
    showNotificationFn('info', title, message, duration);

  // ─────────────────────────────────────────────────────────────
  // QUEUE MANAGEMENT
  // ─────────────────────────────────────────────────────────────
  const addNotification = (type, title, message, duration = 5000) => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, type, title, message, duration }]);
    
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, duration + 500);
    
    return id;
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const clearAllNotifications = () => {
    setNotifications([]);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const value = {
    isNotificationVisible, // ✅ Renamed
    notificationData,
    notifications,
    showNotification: showNotificationFn, // ✅ Function only
    hideNotification,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    addNotification,
    removeNotification,
    clearAllNotifications
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

// ─────────────────────────────────────────────────────────────
// NOTIFICATION TOAST COMPONENT (Theme-Aware)
// ─────────────────────────────────────────────────────────────
export const NotificationToast = () => {
  // ✅ Use renamed state
  const { isNotificationVisible, notificationData, hideNotification } = useNotification();
  const { theme } = useContext(ThemeContext); // ← Theme
  const { type, title, message, duration } = notificationData;

  useEffect(() => {
    if (isNotificationVisible && duration > 0) {
      const timer = setTimeout(() => {
        hideNotification();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [isNotificationVisible, duration, hideNotification]);

  // ✅ FIX: Use renamed state for conditional render
  if (!isNotificationVisible) return null;

  // Get icon based on type
  const getIcon = () => {
    switch (type) {
      case 'success':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
            <polyline points="22 4 12 14.01 9 11.01"/>
          </svg>
        );
      case 'error':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="15" y1="9" x2="9" y2="15"/>
            <line x1="9" y1="9" x2="15" y2="15"/>
          </svg>
        );
      case 'warning':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
        );
      case 'info':
      default:
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="16" x2="12" y2="12"/>
            <line x1="12" y1="8" x2="12.01" y2="8"/>
          </svg>
        );
    }
  };

  // Get theme-aware colors
  const getColors = () => {
    switch (type) {
      case 'success':
        return {
          accent: theme.success,
          bgLight: `${theme.success}15`,
          border: `${theme.success}40`,
          iconBg: `${theme.success}20`
        };
      case 'error':
        return {
          accent: theme.error || theme.danger,
          bgLight: `${theme.error || theme.danger}15`,
          border: `${theme.error || theme.danger}40`,
          iconBg: `${theme.error || theme.danger}20`
        };
      case 'warning':
        return {
          accent: theme.warning,
          bgLight: `${theme.warning}15`,
          border: `${theme.warning}40`,
          iconBg: `${theme.warning}20`
        };
      case 'info':
      default:
        return {
          accent: theme.primary,
          bgLight: `${theme.primary}15`,
          border: `${theme.primary}40`,
          iconBg: `${theme.primary}20`
        };
    }
  };

  const colors = getColors();

  return (
    <div
      style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: 9999,
        animation: 'slideInRight 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
        maxWidth: '450px',
        width: 'calc(100% - 40px)',
      }}
    >
      <div
        style={{
          background: theme.surface,
          borderRadius: '12px',
          boxShadow: `0 10px 40px ${theme.primary}20, 0 2px 8px rgba(0,0,0,0.1)`,
          border: `1px solid ${colors.border}`,
          overflow: 'hidden',
          padding: '16px 20px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
          position: 'relative',
        }}
      >
        {/* Accent Bar */}
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: '4px',
            background: colors.accent,
            borderRadius: '12px 0 0 12px',
          }}
        />

        {/* Icon */}
        <div
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            background: colors.iconBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            color: colors.accent,
            marginLeft: '4px',
          }}
        >
          {getIcon()}
        </div>

        {/* Content */}
        <div style={{ flex: 1, minWidth: 0, paddingTop: '2px' }}>
          {title && (
            <div
              style={{
                fontSize: '14px',
                fontWeight: 700,
                color: theme.textPrimary,
                marginBottom: '4px',
              }}
            >
              {title}
            </div>
          )}
          <div
            style={{
              fontSize: '13px',
              color: theme.textSecondary,
              lineHeight: '1.5',
              wordBreak: 'break-word',
            }}
          >
            {message}
          </div>
        </div>

        {/* Close Button */}
        <button
          onClick={hideNotification}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: theme.textMuted,
            padding: '4px',
            borderRadius: '6px',
            transition: 'all 0.2s',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = theme.surfaceLighter;
            e.currentTarget.style.color = theme.textPrimary;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = theme.textMuted;
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>

      {/* Progress Bar */}
      <div
        style={{
          height: '3px',
          background: theme.surfaceLighter,
          borderRadius: '0 0 12px 12px',
          overflow: 'hidden',
          marginTop: '2px',
        }}
      >
        <div
          style={{
            height: '100%',
            background: colors.accent,
            animation: `progressBar ${duration}ms linear forwards`,
          }}
        />
      </div>

      <style>{`
        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(100px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateX(0) scale(1);
          }
        }
        @keyframes progressBar {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// NOTIFICATION LIST COMPONENT (Theme-Aware)
// ─────────────────────────────────────────────────────────────
export const NotificationList = () => {
  const { notifications, removeNotification } = useNotification();
  const { theme } = useContext(ThemeContext);

  if (notifications.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: theme.textMuted }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>🔔</div>
        <p style={{ margin: 0 }}>No notifications</p>
      </div>
    );
  }

  const getTypeColor = (type) => {
    switch (type) {
      case 'success': return theme.success;
      case 'error': return theme.error || theme.danger;
      case 'warning': return theme.warning;
      default: return theme.primary;
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'success': return '✅';
      case 'error': return '❌';
      case 'warning': return '⚠️';
      default: return 'ℹ️';
    }
  };

  return (
    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
      {notifications.map((notif) => (
        <div
          key={notif.id}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '12px 16px',
            borderBottom: `1px solid ${theme.border}`,
            transition: 'background 0.2s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = theme.surfaceHover || theme.surfaceLighter; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
        >
          <span style={{ fontSize: '20px' }}>{getTypeIcon(notif.type)}</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '14px', fontWeight: 600, color: theme.textPrimary }}>
              {notif.title}
            </div>
            <div style={{ fontSize: '12px', color: theme.textSecondary, marginTop: 2 }}>
              {notif.message}
            </div>
          </div>
          <button
            onClick={() => removeNotification(notif.id)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: theme.textMuted,
              padding: '4px',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = theme.error || theme.danger;
              e.currentTarget.style.background = `${theme.error || theme.danger}15`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = theme.textMuted;
              e.currentTarget.style.background = 'transparent';
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
      ))}
    </div>
  );
};

export default NotificationProvider;